import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import type { User } from '../../../types';
import type {
  ChangeCatalogItem,
  ChangeDateRevision,
  ChangeFormField,
  ChangeType,
} from '../../../types/changes';
import { isEmergencyChangeType, TYPE_LABELS } from '../changeLabels';
import type { ChangeExtraFields, ChangeExtraValue } from '../../../types/changes';
import { dateRevisionOf } from '../changeDateRevisions';
import { configurableFieldHelpText, extraFieldVisible, weekdayConstraintHint } from '../changeFormSchema';
import {
  CHANGE_FIELD_LIMITS,
  formSkipsAutoChecklist,
  needsGoLiveOverride,
  patchGoLiveStart,
  type ChangeFormValues,
  type GoLiveOverrideState,
} from '../changeValidation';
import {
  DEFAULT_CHANGE_WINDOW_WEEKDAY,
  nativeWeekdayConstraint,
  requiresThursdayGoLive,
  toLocalDateInput,
  weekdayName,
} from '../goLiveWindow';
import CatalogSelect from './CatalogSelect';
import ChangeDateRevisionHistory from './ChangeDateRevisionHistory';
import ChangeExtraFieldInput from './ChangeExtraFieldInput';
import ChangeUserPickerInput from './ChangeUserPickerInput';
import ChangeImmediateManagerPickInput from './ChangeImmediateManagerPickInput';
import SearchableSelect from './SearchableSelect';

interface Props {
  idPrefix: string;
  fields: ChangeFormField[];
  form: ChangeFormValues & { change_owner_id?: number; requester_id?: number };
  extras: ChangeExtraFields;
  onFormChange: (
    patch: Partial<ChangeFormValues & { change_owner_id?: number; requester_id?: number }>,
  ) => void;
  onExtraChange: (fieldId: number, value: ChangeExtraValue) => void;
  categories: ChangeCatalogItem[];
  requesterAreas: ChangeCatalogItem[];
  taskTypes?: ChangeCatalogItem[];
  users?: User[];
  override: GoLiveOverrideState;
  onOverrideChange: (patch: Partial<Omit<GoLiveOverrideState, 'isAdmin'>>) => void;
  currentCategory?: { id: number; name: string } | null;
  currentRequesterArea?: { id: number; name: string } | null;
  showPeopleFields?: boolean;
  showTaskTypes?: boolean;
  canChooseRequester?: boolean;
  canChooseOwner?: boolean;
  /** Tipos ativos do cadastro; se vazio, usa rótulos fixos legados. */
  typeOptions?: Array<{ value: string; label: string }>;
  dateRevisions?: Record<string, ChangeDateRevision>;
}

const colProps = (span: number) => (span === 6 ? { xs: 12 as const, md: 6 as const } : { xs: 12 as const });

const ImmediateManagerFieldCol: React.FC<{
  field: ChangeFormField;
  idPrefix: string;
  form: ChangeFormValues & { change_owner_id?: number; requester_id?: number };
  onFormChange: Props['onFormChange'];
  dateRevisions?: Props['dateRevisions'];
}> = ({ field, idPrefix, form, onFormChange, dateRevisions }) => {
  const [applicable, setApplicable] = useState<boolean | null>(null);
  const requesterId = form.requester_id || 0;
  useEffect(() => {
    setApplicable(null);
  }, [requesterId, form.change_type]);
  useEffect(() => {
    if (applicable === false && (form.requester_manager_group_id || 0) > 0) {
      onFormChange({ requester_manager_group_id: 0 });
    }
  }, [applicable, form.requester_manager_group_id, onFormChange]);
  if (applicable === false) return null;
  const helpText = configurableFieldHelpText(field);
  return (
    <Col {...colProps(field.col_span)}>
      <Form.Label>{field.label}</Form.Label>
      <ChangeImmediateManagerPickInput
        id={`${idPrefix}-immediate-manager-pick`}
        label={field.label}
        requesterId={requesterId}
        changeType={form.change_type}
        value={form.requester_manager_group_id || 0}
        onChange={(requester_manager_group_id) => onFormChange({ requester_manager_group_id })}
        onApplicableKnown={setApplicable}
      />
      <ChangeDateRevisionHistory showRemaining revision={dateRevisionOf(dateRevisions, field)} />
      {helpText ? <Form.Text className="text-muted d-block">{helpText}</Form.Text> : null}
      {weekdayConstraintHint(field.weekday_constraint) ? (
        <Form.Text className="text-muted d-block">
          {weekdayConstraintHint(field.weekday_constraint)}
        </Form.Text>
      ) : null}
    </Col>
  );
};

const ChangeConfigurableForm: React.FC<Props> = ({
  idPrefix,
  fields,
  form,
  extras,
  onFormChange,
  onExtraChange,
  categories,
  requesterAreas,
  taskTypes = [],
  users = [],
  override,
  onOverrideChange,
  currentCategory,
  currentRequesterArea,
  showPeopleFields = true,
  showTaskTypes = true,
  canChooseRequester = true,
  canChooseOwner = false,
  typeOptions = [],
  dateRevisions,
}) => {
  const changeTypeChoices =
    typeOptions.length > 0
      ? typeOptions
      : (Object.keys(TYPE_LABELS) as ChangeType[]).map((value) => ({
          value,
          label: TYPE_LABELS[value] || value,
        }));
  const emergency = isEmergencyChangeType(form.change_type);
  const skipChecklist = formSkipsAutoChecklist(fields, form, extras);
  const hiddenBySkip = (nativeKey?: string | null) =>
    skipChecklist && nativeKey === 'risk_committee_window';
  const needsThursday = requiresThursdayGoLive(form.change_type);
  const goLiveWeekday = nativeWeekdayConstraint(
    fields,
    'go_live_window',
    DEFAULT_CHANGE_WINDOW_WEEKDAY,
  );
  const outsideWindow = needsGoLiveOverride(form, fields);
  const goLiveDayName = weekdayName(goLiveWeekday) || 'quinta-feira';
  const endBeforeStart =
    Boolean(form.go_live_start_at) &&
    Boolean(form.go_live_end_at) &&
    new Date(form.go_live_end_at) < new Date(form.go_live_start_at);

  const renderNative = (field: ChangeFormField) => {
    const key = field.native_key;
    const label = field.label;
    if (key === 'change_owner_id' || key === 'requester_id') {
      if (!showPeopleFields) return null;
      const value = key === 'change_owner_id' ? form.change_owner_id : form.requester_id;
      const peopleDisabled =
        (key === 'requester_id' && !canChooseRequester) ||
        (key === 'change_owner_id' && !canChooseOwner);
      if (field.select_search_enabled) {
        return (
          <ChangeUserPickerInput
            ariaLabel={label}
            value={value || 0}
            disabled={peopleDisabled}
            onChange={(userId) =>
              onFormChange(
                key === 'change_owner_id'
                  ? { change_owner_id: userId }
                  : { requester_id: userId },
              )
            }
          />
        );
      }
      return (
        <Form.Select
          aria-label={label}
          value={value || ''}
          disabled={peopleDisabled}
          onChange={(e) => {
            const nextId = Number(e.target.value);
            if (key === 'change_owner_id') {
              onFormChange({ change_owner_id: nextId });
              return;
            }
            onFormChange({ requester_id: nextId, requester_manager_group_id: 0 });
          }}
        >
          <option value="">Selecione</option>
          {users.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Form.Select>
      );
    }
    if (key === 'title') {
      return (
        <Form.Control
          aria-label={label}
          value={form.title}
          maxLength={CHANGE_FIELD_LIMITS.title}
          onChange={(e) => onFormChange({ title: e.target.value })}
        />
      );
    }
    if (key === 'change_type') {
      return (
        <SearchableSelect
          ariaLabel={label}
          value={form.change_type}
          searchEnabled={Boolean(field.select_search_enabled)}
          options={changeTypeChoices.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={(next) => {
            const change_type = String(next) as ChangeType;
            onFormChange(
              isEmergencyChangeType(change_type)
                ? {
                    change_type,
                    risk_committee_start_at: '',
                    risk_committee_end_at: '',
                    camud_go_no_go_date: '',
                  }
                : { change_type },
            );
          }}
        />
      );
    }
    if (key === 'project') {
      return (
        <Form.Control
          aria-label={label}
          value={form.project}
          maxLength={CHANGE_FIELD_LIMITS.shortText}
          onChange={(e) => onFormChange({ project: e.target.value })}
        />
      );
    }
    if (key === 'category_id') {
      return (
        <CatalogSelect
          id={`${idPrefix}-category`}
          label={label}
          items={categories}
          value={form.category_id}
          current={currentCategory}
          searchEnabled={Boolean(field.select_search_enabled)}
          onChange={(category_id) => onFormChange({ category_id })}
        />
      );
    }
    if (key === 'requester_area_id') {
      return (
        <CatalogSelect
          id={`${idPrefix}-requester-area`}
          label={label}
          items={requesterAreas}
          value={form.requester_area_id}
          current={currentRequesterArea}
          searchEnabled={Boolean(field.select_search_enabled)}
          onChange={(requester_area_id) => onFormChange({ requester_area_id })}
        />
      );
    }
    if (key === 'requester_manager_group_id') {
      return null;
    }
    if (key === 'sap_classification') {
      return (
        <div>
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}-sap-sap`}
            name={`${idPrefix}-sap-classification`}
            label="SAP"
            checked={form.sap_classification === 'sap'}
            onChange={() =>
              onFormChange({ sap_classification: 'sap', sap_has_request: form.sap_has_request ?? null })
            }
          />
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}-sap-nao`}
            name={`${idPrefix}-sap-classification`}
            label="Não SAP"
            checked={form.sap_classification === 'nao_sap'}
            onChange={() => onFormChange({ sap_classification: 'nao_sap', sap_has_request: null })}
          />
        </div>
      );
    }
    if (key === 'sap_has_request') {
      return (
        <div>
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}-sap-request-sim`}
            name={`${idPrefix}-sap-has-request`}
            label="Sim"
            checked={form.sap_has_request === true}
            onChange={() => onFormChange({ sap_has_request: true })}
          />
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}-sap-request-nao`}
            name={`${idPrefix}-sap-has-request`}
            label="Não"
            checked={form.sap_has_request === false}
            onChange={() => onFormChange({ sap_has_request: false })}
          />
        </div>
      );
    }
    if (key === 'task_type_ids') {
      if (!showTaskTypes) return null;
      const selected = form.task_type_ids || [];
      if (!taskTypes.length) {
        return (
          <Form.Text className="text-muted d-block">
            Nenhum tipo de tarefa ativo. Cadastre em Catálogos.
          </Form.Text>
        );
      }
      return (
        <div>
          {taskTypes.map((item) => (
            <Form.Check
              key={item.id}
              type="checkbox"
              id={`${idPrefix}-task-type-${item.id}`}
              label={item.name}
              checked={selected.includes(item.id)}
              onChange={(e) =>
                onFormChange({
                  task_type_ids: e.target.checked
                    ? [...selected, item.id]
                    : selected.filter((value) => value !== item.id),
                })
              }
            />
          ))}
        </div>
      );
    }
    if (key === 'go_live_window') {
      return (
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Label htmlFor={`${idPrefix}-go-live-start`}>
              {emergency ? 'Início da implementação' : 'Início do Go Live'}
            </Form.Label>
            <Form.Control
              id={`${idPrefix}-go-live-start`}
              type="datetime-local"
              value={form.go_live_start_at}
              disabled={Boolean(dateRevisionOf(dateRevisions, field)?.locked)}
              onChange={(e) => onFormChange(patchGoLiveStart(form, e.target.value))}
            />
            {needsThursday ? (
              <Form.Text className={outsideWindow ? 'text-danger' : 'text-muted'}>
                Normal/Padrão: Go Live deve começar numa quinta-feira.
              </Form.Text>
            ) : null}
          </Col>
          <Col xs={12} md={6}>
            <Form.Label htmlFor={`${idPrefix}-go-live-end`}>
              {emergency ? 'Fim da implementação' : 'Fim do Go Live'}
            </Form.Label>
            <Form.Control
              id={`${idPrefix}-go-live-end`}
              type="datetime-local"
              value={form.go_live_end_at}
              min={form.go_live_start_at || undefined}
              disabled={Boolean(dateRevisionOf(dateRevisions, field)?.locked)}
              onChange={(e) => onFormChange({ go_live_end_at: e.target.value })}
            />
            {endBeforeStart ? (
              <Form.Text className="text-danger">O fim precisa ser maior ou igual ao início.</Form.Text>
            ) : null}
          </Col>
        </Row>
      );
    }
    if (key === 'risk_committee_window') {
      return (
        <Form.Control
          id={`${idPrefix}-risk-start`}
          aria-label={label}
          type="date"
          value={toLocalDateInput(form.risk_committee_start_at)}
          disabled={Boolean(dateRevisionOf(dateRevisions, field)?.locked)}
          onChange={(e) =>
            onFormChange({
              risk_committee_start_at: toLocalDateInput(e.target.value),
              risk_committee_end_at: toLocalDateInput(e.target.value),
            })
          }
        />
      );
    }
    if (key === 'camud_go_no_go_date') {
      return (
        <Form.Control
          id={`${idPrefix}-camud-date`}
          aria-label={label}
          type="date"
          value={toLocalDateInput(form.camud_go_no_go_date)}
          disabled={Boolean(dateRevisionOf(dateRevisions, field)?.locked)}
          onChange={(e) => onFormChange({ camud_go_no_go_date: toLocalDateInput(e.target.value) })}
        />
      );
    }
    if (key === 'description' || key === 'scope' || key === 'impacted_users' || key === 'rollback_plan') {
      const value =
        key === 'description'
          ? form.description
          : key === 'scope'
            ? form.scope
            : key === 'impacted_users'
              ? form.impacted_users
              : form.rollback_plan;
      return (
        <Form.Control
          as="textarea"
          rows={2}
          aria-label={label}
          value={value}
          maxLength={CHANGE_FIELD_LIMITS.longText}
          onChange={(e) => onFormChange({ [key]: e.target.value } as Partial<ChangeFormValues>)}
        />
      );
    }
    return null;
  };

  const renderExtra = (field: ChangeFormField) => (
    <ChangeExtraFieldInput
      field={field}
      value={extras[String(field.id)]}
      disabled={Boolean(dateRevisionOf(dateRevisions, field)?.locked)}
      onChange={(value) => onExtraChange(field.id, value)}
    />
  );

  let lastSection = '';
  return (
    <Row className="g-3">
      {fields.map((field) => {
        if (field.kind === 'native' && hiddenBySkip(field.native_key)) return null;
        if (field.kind === 'native' && !extraFieldVisible(field, fields, form, extras)) return null;
        if (field.kind === 'native' && field.native_key === 'task_type_ids' && !showTaskTypes) return null;
        if (
          field.kind === 'native' &&
          (field.native_key === 'change_owner_id' || field.native_key === 'requester_id') &&
          !showPeopleFields
        ) {
          return null;
        }
        if (field.kind === 'native' && !field.is_visible && !field.is_locked) return null;
        if (field.kind === 'extra' && !extraFieldVisible(field, fields, form, extras)) return null;
        if (field.kind === 'native' && field.native_key === 'requester_manager_group_id') {
          return (
            <ImmediateManagerFieldCol
              key={field.id}
              field={field}
              idPrefix={idPrefix}
              form={form}
              onFormChange={onFormChange}
              dateRevisions={dateRevisions}
            />
          );
        }
        const section = field.section_name || '';
        const showSection = Boolean(section && section !== lastSection);
        if (section) lastSection = section;
        const hideLabel =
          field.native_key === 'category_id' ||
          field.native_key === 'requester_area_id' ||
          field.native_key === 'go_live_window';
        const helpText = configurableFieldHelpText(field);
        return (
          <React.Fragment key={field.id}>
            {showSection ? (
              <Col xs={12}>
                <h6 className="mb-0">{section}</h6>
              </Col>
            ) : null}
            <Col {...colProps(field.col_span)}>
              {hideLabel ? null : <Form.Label>{field.label}</Form.Label>}
              {field.kind === 'native' ? renderNative(field) : renderExtra(field)}
              <ChangeDateRevisionHistory
                showRemaining
                revision={dateRevisionOf(dateRevisions, field)}
              />
              {helpText ? <Form.Text className="text-muted d-block">{helpText}</Form.Text> : null}
              {weekdayConstraintHint(field.weekday_constraint) ? (
                <Form.Text className="text-muted d-block">
                  {weekdayConstraintHint(field.weekday_constraint)}
                </Form.Text>
              ) : null}
            </Col>
          </React.Fragment>
        );
      })}
      {outsideWindow && override.isAdmin ? (
        <Col xs={12}>
          <Form.Check
            type="checkbox"
            id={`${idPrefix}-force-go-live`}
            label={`Forçar Go Live fora de ${goLiveDayName} (admin)`}
            checked={override.forceOutside}
            onChange={(e) => onOverrideChange({ forceOutside: e.target.checked })}
          />
          {override.forceOutside ? (
            <Form.Control
              className="mt-2"
              placeholder="Motivo do force"
              maxLength={CHANGE_FIELD_LIMITS.longText}
              value={override.forceReason}
              onChange={(e) => onOverrideChange({ forceReason: e.target.value })}
            />
          ) : null}
        </Col>
      ) : null}
    </Row>
  );
};

export default ChangeConfigurableForm;
